/*
 * The one settings dialog every tile shares.
 *
 * A widget does not draw its own settings: it DECLARES them — a short schema in
 * its meta (`settings: [{ key, label, kind, ... }]`) — and the base widget owns
 * the gear button, this dialog, and the save. The same form fields serve a
 * sandbox widget, a native tile and, later, whatever the AI author writes,
 * which is the point: one contract, one look, no widget with a form of its own.
 *
 * Kinds: number · text · boolean · select · window (a time-window preset, in
 * seconds) · list (rows of fields — the symbols a widget binds, the items it
 * shows — with add and remove).
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { LocaleController } from '@vitesse/base-ui/i18n';
import { button, iconButton } from '@vitesse/base-ui/components/base-buttons';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/inputs/vitesse-select';
import '@vitesse/base-ui/elements/inputs/vitesse-text-input';
import '@vitesse/base-ui/elements/inputs/vitesse-number-input';
import '@vitesse/base-ui/elements/inputs/vitesse-toggle';
import type { SettingSpec } from '../../dashboards/runtime/sdk';
import { TIME_WINDOWS } from '../components/chart/chart-types';

type Row = Record<string, unknown>;

@customElement('vitesse-widget-config-dialog')
export class VitesseWidgetConfigDialog extends LitElement {
  @property({ type: Boolean }) open = false;
  @property({ type: String }) heading = '';
  @property({ attribute: false }) schema: SettingSpec[] = [];
  @property({ attribute: false }) config: Record<string, unknown> = {};
  /** The controllers a symbol can belong to: a controller field becomes a picker. */
  @property({ attribute: false }) controllers: { value: string; label: string }[] = [];

  @state() private draft: Record<string, unknown> = {};

  constructor() {
    super();
    new LocaleController(this); // every label is t()'d — re-render on a language switch
  }

  createRenderRoot(): HTMLElement { return this; }

  willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open) this.draft = structuredClone(this.config);
  }

  private set(key: string, value: unknown): void {
    this.draft = { ...this.draft, [key]: value };
  }

  private save(): void {
    this.dispatchEvent(new CustomEvent('settings-save', { detail: this.draft, bubbles: true, composed: true }));
  }

  private close(): void {
    this.dispatchEvent(new CustomEvent('settings-close', { bubbles: true, composed: true }));
  }

  private control(spec: SettingSpec, value: unknown, onChange: (v: unknown) => void) {
    const pick = (options: { value: string; label: string }[], v: string, on: (x: string) => void) =>
      html`<vitesse-select size="sm" .options=${options} .value=${v} @change=${(e: CustomEvent<{ value: string }>) => { e.stopPropagation(); on(e.detail.value); }}></vitesse-select>`;
    if (spec.key === 'controllerId' && this.controllers.length) return pick(this.controllers, String(value ?? ''), (x) => onChange(x));
    switch (spec.kind) {
      case 'number':
        return html`<vitesse-number-input size="sm" .value=${value == null ? '' : String(value)} .min=${spec.min} .max=${spec.max} .step=${spec.step ?? 1} ?integer=${spec.step === 1}
          @change=${(e: CustomEvent<{ value: string }>) => { e.stopPropagation(); const x = e.detail.value; onChange(x === '' ? null : Number(x)); }}></vitesse-number-input>`;
      case 'boolean':
        return html`<vitesse-toggle size="sm" .checked=${!!value} @change=${(e: CustomEvent<{ checked: boolean }>) => { e.stopPropagation(); onChange(e.detail.checked); }}></vitesse-toggle>`;
      case 'select':
        return pick((spec.options ?? []).map((o) => ({ value: String(o.value), label: o.label })), String(value ?? ''), (x) => { const o = (spec.options ?? []).find((q) => String(q.value) === x); onChange(o ? o.value : x); });
      case 'window':
        return pick(TIME_WINDOWS.map((w) => ({ value: String(w.value / 1000), label: t('chart.lastWindow', { window: w.label }) })), String(value ?? 300), (x) => onChange(Number(x)));
      default:
        return html`<vitesse-text-input size="sm" .value=${value == null ? '' : String(value)} @change=${(e: CustomEvent<{ value: string }>) => { e.stopPropagation(); onChange(e.detail.value); }}></vitesse-text-input>`;
    }
  }

  private list(spec: SettingSpec) {
    const rows = Array.isArray(this.draft[spec.key]) ? (this.draft[spec.key] as Row[]) : [];
    const fields = spec.fields ?? [];
    const update = (i: number, key: string, v: unknown) => this.set(spec.key, rows.map((r, k) => (k === i ? { ...r, [key]: v } : r)));
    return html`<div class="wcfg-list">
      <div class="wcfg-list-head">${fields.map((f) => html`<span>${f.label}</span>`)}<span></span></div>
      ${rows.map((r, i) => html`<div class="wcfg-list-row">
        ${fields.map((f) => html`<span>${this.control(f, r[f.key], (v) => update(i, f.key, v))}</span>`)}
        <span class="wcfg-del">${iconButton({ icon: 'trash-2', title: t('dashboards.settingsRemoveRow'), size: 16, danger: true, onClick: () => this.set(spec.key, rows.filter((_, k) => k !== i)) })}</span>
      </div>`)}
      ${button({ label: t('dashboards.settingsAddRow'), icon: 'plus', size: 'sm', onClick: () => this.set(spec.key, [...rows, Object.fromEntries(fields.map((f) => [f.key, f.kind === 'number' ? null : f.kind === 'boolean' ? false : '']))]) })}
    </div>`;
  }

  /** One bound point: its fields on a row, no add or remove. */
  private symbol(spec: SettingSpec) {
    const obj = (this.draft[spec.key] && typeof this.draft[spec.key] === 'object' ? this.draft[spec.key] : {}) as Row;
    const fields = spec.fields ?? [];
    return html`<div class="wcfg-list">
      <div class="wcfg-list-head wcfg-one">${fields.map((f) => html`<span>${f.label}</span>`)}</div>
      <div class="wcfg-list-row wcfg-one">${fields.map((f) => html`<span>${this.control(f, obj[f.key], (v) => this.set(spec.key, { ...obj, [f.key]: v }))}</span>`)}</div>
    </div>`;
  }

  render() {
    if (!this.open) return nothing;
    const wide = this.schema.some((s) => (s.kind === 'list' || s.kind === 'symbol') && (s.fields?.length ?? 0) > 4);
    return html`<vitesse-dialog open .heading=${(this.heading ? this.heading + ' · ' : '') + t('dashboards.tileSettings')} .width=${wide ? '760px' : '480px'} @dialog-close=${this.close}>
      <div class="dialog-form wcfg">
        ${this.schema.map((spec) => html`<div class="form-group ${spec.kind === 'list' || spec.kind === 'symbol' ? 'wcfg-stacked' : ''}">
          <label class="wcfg-label">${spec.label}</label>
          ${spec.kind === 'list' ? this.list(spec) : spec.kind === 'symbol' ? this.symbol(spec) : this.control(spec, this.draft[spec.key], (v) => this.set(spec.key, v))}
          ${spec.hint ? html`<div class="field-help">${spec.hint}</div>` : nothing}
        </div>`)}
      </div>
      <div slot="footer" class="dialog-actions">
        ${button({ label: t('dashboards.settingsCancel'), size: 'sm', onClick: () => this.close() })}
        ${button({ label: t('dashboards.settingsSave'), color: 'blue', size: 'sm', onClick: () => this.save() })}
      </div>
    </vitesse-dialog>`;
  }
}
